import { useMemo } from "react";

import {
  SubmissionForm,
  TextInputField,
  ToggleField,
  Heading,
  Stack,
  Text,
  useTheme,
  UseFormMethods
} from "@bps/fluent-ui";

import { Validator } from "@bps/validator";
import { BpMobileMaintenanceSettings } from "@libs/api/gateways/bp-mobile/bp-mobile.dtos";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";
import { NUMBER_FORMAT_REGEX } from "@libs/validation/validation-rules.constants";

export interface BpMobileMaintenanceFormSubmitValues {
  maintenanceMode: boolean;
  enableFieldManagement: boolean;
  maintenanceModeMessage: string;
  forceUpgradeVersionNumber: string;
  forceUpgradeMessage: string;
}

interface BpMobileMaintenanceFormProps {
  onSubmit: (
    values: BpMobileMaintenanceFormSubmitValues,
    actions: Pick<
      UseFormMethods<BpMobileMaintenanceFormSubmitValues>,
      "reset" | "setValue"
    >
  ) => Promise<void>;
  settings: BpMobileMaintenanceSettings;
}

const mobileMaintValidator = new Validator<BpMobileMaintenanceFormSubmitValues>(
  validator => ({
    forceUpgradeVersionNumber: [
      validator.string().required({ message: "Version number is required" }),
      validator.string().pattern(NUMBER_FORMAT_REGEX, {
        message: "Version must be in the format X.YY (e.g., 1.00)"
      })
    ]
  })
);

export const BpMobileMaintenanceForm: React.FC<BpMobileMaintenanceFormProps> = ({
  onSubmit,
  settings
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.BhMobileSettingsWrite
  );

  const defaultValues: BpMobileMaintenanceFormSubmitValues = useMemo(() => {
    return {
      maintenanceMode: settings?.maintenanceMode ?? false,
      maintenanceModeMessage: settings?.maintenanceModeMessage ?? "",
      forceUpgradeVersionNumber: settings?.forceUpgradeVersionNumber ?? "",
      forceUpgradeMessage: settings?.forceUpgradeMessage ?? "",
      enableFieldManagement: settings?.enableFieldManagement ?? false
    };
  }, [settings]);

  const theme = useTheme();

  return (
    <SubmissionForm<BpMobileMaintenanceFormSubmitValues>
      hideButtons={!hasWritePermission}
      disabled={!hasWritePermission}
      onSubmit={onSubmit}
      initialValues={defaultValues}
      validate={mobileMaintValidator.validate}
    >
      {() => (
        <>
          <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
            <Heading variant="section-heading">BP Mobile App Settings</Heading>

            <ToggleField name="maintenanceMode" label="Maintenance Mode" />

            <TextInputField
              name="maintenanceModeMessage"
              label="Maintenance Mode Message"
              multiline
            />

            <TextInputField
              name="forceUpgradeVersionNumber"
              label="Force Upgrade Version Number"
            />

            <TextInputField
              name="forceUpgradeMessage"
              label="Force Upgrade Message"
              multiline
            />
          </Stack>
          <Text variant="xLarge">Field Management Settings</Text>
          <ToggleField
            name="enableFieldManagement"
            label="Field Management Enable"
          />
        </>
      )}
    </SubmissionForm>
  );
};
